import React, { createRef, FC, useEffect, useRef } from 'react'
import {
  Box,
  ButtonOutline,
  Flex,
  FlexBoxGrid,
  Link
} from 'front-end/src/components'
import { Trans, t, Plural } from '@lingui/macro'
import { css } from '@emotion/react'

import CardGrid from '../../components/common/card-grid/CardGrid'
import ErrorMessage from '../../components/common/error/ErrorMessage'
import HeadTitle from '../../components/common/head/HeadTitle'
import IconSettings from 'front-end/src/components/common/icon/IconSettings'
import PageHeader from '../../components/common/page/header/PageHeader'
import { Bookmark } from './components/bookmark/Bookmark'
import { ExpandableCardGrid } from '../../components/common/expandable-card-grid/ExpandableCardGrid'
import {
  ITopicEnrolment,
  ResourceCard,
  resourceCardCoverImageSrcSet
} from './resource-card/ResourceCard'
import { linkBuilder } from 'shared/linkBuilder'
import { navigateTo } from 'front-end/src/app/util/appHistory'
import { Page } from '../../components/common/page/Page'
import {
  TopicModelAsMember,
  UserModelPublicBase
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import {
  useBookmarkListQuery,
  useWorkspaceTopicsAndThemesQuery
} from './workspacePage.codegen'
import './WorkspacePage.less'
import { DangerouslySetInnerHTML } from '../../utils/DangerouslySetInnerHTML'
import Tag from 'front-end/src/components/common/tag/Tag'
import { brandColors } from 'shared/basicPalette'
import { getQuillStyle } from 'front-end/src/components/common/quill/EditorQuill'
import { groupBy } from 'lodash'
import Loader from 'front-end/src/components/common/loader/Loader'
import { useLocation, useParams } from 'react-router-dom'
import { HideOnMobileDevice } from '../../components/common/HideOnMobileDevice'
import EmptyResourcesLogo from 'public/assets/img/empty-resources.svg'
import IconTag from 'front-end/src/components/common/icon/IconTag'
import { scrollToRef } from 'front-end/src/utils/scrollHelpers'

export let latestVisitedWorkspaceTopic: number | null = null

export const setLatestVisitedWorkspaceTopic = (topicId: number) => {
  latestVisitedWorkspaceTopic = topicId
}

const TopicCardGrid: FC<{
  topics: Array<Partial<TopicModelAsMember>>
  themeTitle?: string
  themeId?: number
}> = ({ topics, themeTitle, themeId }) => {
  const topicResourceCards = topics.map((topic) => {
    const {
      author,
      currentTitle,
      currentTopicVersion,
      id,
      coverImage,
      lastEnrolment
    } = topic

    let image: any = null
    if (coverImage) {
      const resizedImage = coverImage.imageVariants.find(
        (imageVariant) => imageVariant.size_key === '300x200'
      )
      image = resizedImage ? resizedImage.cdnUrl : coverImage.cdnUrl
    }

    return (
      <ResourceCard
        key={id}
        title={currentTitle}
        description={currentTopicVersion?.summary || ''}
        enrolment={lastEnrolment as ITopicEnrolment}
        topicId={id as number}
        author={author as UserModelPublicBase}
        readDurationMins={currentTopicVersion?.readDurationMins ?? null}
        image={image || undefined}
        imageSrcSet={resourceCardCoverImageSrcSet(coverImage?.imageVariants)}
      />
    )
  })

  if (themeTitle) {
    return (
      <ExpandableCardGrid
        cards={topicResourceCards}
        title={themeTitle}
        copyAnchorId={`${themeId}`}
        renderShowMore={(remainingCards) => {
          return (
            <Plural
              value={remainingCards}
              one="Show # more resource"
              other="Show # more resources"
            />
          )
        }}
      />
    )
  } else {
    return <CardGrid cards={topicResourceCards} />
  }
}

let autoscrollAwaited = false

export const WorkspacePage: FC = () => {
  const themeRefs = useRef<Record<string, any>>()
  const descriptionRef = useRef<HTMLDivElement>(null)
  const { hash, pathname } = useLocation()
  const hashThemeId = hash.replace(/^#/, '') || ''
  const { workspaceId: workspaceIdString } =
    useParams<{ workspaceId: string }>()
  const workspaceId = Number(workspaceIdString)
  const { data, loading } = useWorkspaceTopicsAndThemesQuery({
    variables: {
      workspaceId
    }
  })

  const getAnchorRef = (hash = hashThemeId) =>
    themeRefs?.current?.[hash]?.current

  useEffect(() => {
    if (!loading) {
      const descriptionElement = descriptionRef.current
      const anchorRef = getAnchorRef()

      if (anchorRef && descriptionElement) {
        const resizeObserver = new ResizeObserver((entries) => {
          if (entries?.[0] && anchorRef) {
            const event = new CustomEvent('autoscroll', {
              detail: {
                scrollTo: anchorRef
              }
            })
            document.dispatchEvent(event)
          }
        })

        resizeObserver.observe(descriptionElement)

        const customEventHandler = (customEvent: CustomEvent) => {
          if (!customEvent.isTrusted && customEvent.detail?.scrollTo) {
            customEvent.detail.scrollTo.scrollIntoView()
            autoscrollAwaited = true
            return
          }
        }

        const unsubscribe = () => {
          if (autoscrollAwaited) {
            autoscrollAwaited = false
            return
          }
          resizeObserver.disconnect()
          document.removeEventListener('scroll', unsubscribe)
          document.removeEventListener(
            'autoscroll',
            customEventHandler as EventListener
          )
        }

        document.addEventListener(
          'autoscroll',
          customEventHandler as EventListener
        )
        document.addEventListener('scroll', unsubscribe)

        return () => {
          resizeObserver.disconnect()
          document.removeEventListener('scroll', unsubscribe)
          document.removeEventListener(
            'autoscroll',
            customEventHandler as EventListener
          )
        }
      }
    }
  }, [loading])

  useEffect(() => {
    const ref = getAnchorRef()
    if (ref && !loading) {
      scrollToRef(ref)
    }
  }, [hash])

  if (loading) {
    return <Loader />
  }

  if (!data?.workspace) {
    return <ErrorMessage message={t`Workspace wasn't found`} />
  }
  const { workspace } = data
  const canUserManageWorkspace =
    workspace.__typename === 'WorkspaceModelAsCollaborator' ||
    workspace.__typename === 'WorkspaceModelAsCoordinator'

  if (!themeRefs.current) {
    themeRefs.current = {}
    workspace?.themes.forEach((theme) => {
      if (themeRefs.current) {
        themeRefs.current[theme.id] = createRef()
      }
    })
  }

  const renderControls = () => {
    if (data && canUserManageWorkspace) {
      return (
        <HideOnMobileDevice>
          <ButtonOutline
            onClick={() => {
              navigateTo(linkBuilder.workspaceManage(workspace.id))
            }}
            accessibilityTitle={t`Manage workspace`}
            leftIcon={<IconSettings />}
          >
            <Trans>Manage workspace</Trans>
          </ButtonOutline>
        </HideOnMobileDevice>
      )
    }
  }
  let { themes } = workspace

  const topicsNoTheme = workspace.topics.filter((t) => !t.theme_id)
  const topicsByTheme = groupBy(workspace.topics, 'theme_id')
  themes = themes.filter((theme) => theme.topicsCount > 0)

  return (
    <Page className="WorkspacePage" memorizeScroll>
      <HeadTitle title={workspace.name || t`Workspace`} />

      <div className="WorkspacePage__content">
        {data && (
          <>
            <PageHeader
              title={workspace.name}
              className="PageHeader"
              controls={renderControls()}
              subHeader={
                workspace.tags.length > 0 ? (
                  <>
                    <IconTag mr="8px" />
                    {workspace.tags.map((tag) => tag.name).join(', ')}
                  </>
                ) : null
              }
            />
            <Box ref={descriptionRef}>
              {workspace.description && (
                <Flex>
                  <Box
                    className="Workspace__description ql-editor"
                    css={getQuillStyle('ql-editor')}
                  >
                    <DangerouslySetInnerHTML content={workspace.description} />
                  </Box>
                </Flex>
              )}
            </Box>
          </>
        )}

        <BookmarkList workspaceId={workspaceId} />

        {!!themes.length && (
          <Box
            borderBottom="1px solid"
            borderColor={brandColors.grey[200]}
            margin="16px"
          />
        )}
        {/* TODO move into WorkspaceTopThemesList component one day */}
        <Box
          padding={'0 16px'}
          margin={['8px 0', 0]}
          css={css`
            .Tag {
              max-width: 60%;
            }
            .Tag:hover {
              color: ${brandColors.grey.mid};
            }
          `}
        >
          {themes.map((theme) => {
            return (
              <Link
                to={`${pathname}#${theme.id}`}
                key={theme.id}
                onClick={() => {
                  if (hashThemeId === `${theme.id}`) {
                    scrollToRef(getAnchorRef(`${theme.id}`))
                  }
                }}
              >
                <Tag color="clear">{theme.title}</Tag>
              </Link>
            )
          })}
        </Box>

        {/* Workspace topics */}
        <TopicCardGrid topics={topicsNoTheme as any} />

        {/* Workspace themes */}
        {themes.map((theme) => {
          const themeTopics = topicsByTheme[theme.id]
          const hasTopics = themeTopics?.length
          return (
            (hasTopics && (
              <div
                className="WorkspacePage__theme"
                key={theme.id}
                ref={themeRefs.current?.[theme.id]}
                id={theme.id}
              >
                <TopicCardGrid
                  themeTitle={theme.title as string}
                  themeId={theme.id}
                  topics={themeTopics as any}
                />
              </div>
            )) ||
            null
          )
        })}

        {!workspace.topics.length && !workspace.themes.length && (
          <div className="no-items">
            <img src={EmptyResourcesLogo} alt={t`Empty resources`} />
            <h2>
              <Trans>No resources available</Trans>
            </h2>
            <p>
              <Trans>
                There are no resources available to view in this workspace.
              </Trans>
            </p>
          </div>
        )}
      </div>
    </Page>
  )
}

function BookmarkList({ workspaceId }) {
  const { data } = useBookmarkListQuery({
    variables: {
      workspaceId
    }
  })

  const bookmarks =
    data?.workspace?.bookmarkList.map((bookmark) => {
      return (
        <Box key={bookmark.id}>
          <Bookmark data={bookmark} />
        </Box>
      )
    }) || []

  if (!bookmarks.length) {
    return null
  }

  return (
    <FlexBoxGrid
      columns={['300px', '250px', '30%', '30%']}
      columnGap="32px"
      rowGap="16px"
      padding="16px"
      margin="0 -16px"
      css={css`
        clear: both;
      `}
      dataTest="Bookmarks"
    >
      {bookmarks}
    </FlexBoxGrid>
  )
}
